import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import offlineManager from '../../utils/offlineManager';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';

const ProductImage = ({ src, alt, name, className = "w-full h-full object-cover" }) => {
    const [error, setError] = useState(false);
    const [retryWithPort3001, setRetryWithPort3001] = useState(false);

    useEffect(() => {
        setError(false);
        setRetryWithPort3001(false);
    }, [src]);

    const getBaseUrl = () => {
        if (window.location.port === '3000') {
            return `${window.location.protocol}//${window.location.hostname}:3001`;
        }
        return '/api'; // Préfixe nécessaire pour que Nginx proxy vers le backend
    };

    const finalSrc = (src && src.startsWith('/') && !src.startsWith('//'))
        ? `${getBaseUrl()}${src}`
        : src;

    const handleError = () => {
        console.error(`[ProductImage] Erreur de chargement pour : ${finalSrc}`);
        setError(true);
    };

    if (!src || error) {
        return (
            <div className={`${className} bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400`}>
                <span className="material-symbols-outlined text-[20px]">image_not_supported</span>
            </div>
        );
    }

    return (
        <img
            src={finalSrc}
            alt={alt || name}
            className={className}
            onError={handleError}
        />
    );
};

export default function MobileScanner() {
    const { showToast, showConfirm } = useUI();
    const { user } = useAuth();

    // Scanner states
    const [isScanning, setIsScanning] = useState(false);
    const [scannedCode, setScannedCode] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [cameraError, setCameraError] = useState('');
    const html5QrCode = useRef(null);

    // Form & Data states
    const [loading, setLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [offProduct, setOffProduct] = useState(null); // Found on OpenFoodFacts
    const [unknownProduct, setUnknownProduct] = useState(false); // Not found anywhere
    const [searchResults, setSearchResults] = useState([]); // Multiple local matches
    const [warehouses, setWarehouses] = useState([]);
    const [recentMovements, setRecentMovements] = useState([]);
    const [locations, setLocations] = useState([]);
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    const [manualForm, setManualForm] = useState({
        name: '',
        description: ''
    });
    const [capturedImage, setCapturedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        sku: '',
        warehouseId: '',
        locationId: '',
        quantity: 1,
        unit: 'Unité',
        description: ''
    });

    // Connectivity & Sync Effect
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            // Synchronisation manuelle désactivée ici pour éviter les effets de bord
            // L'utilisateur synchronise via l'onglet Configuration
            console.log("[Scanner] Connexion rétablie, synchro automatique désactivée.");
        };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);


        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);


    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            // Load warehouses (filtered by user access or all if admin)
            const warehouseUrl = user.role?.name === 'Admin'
                ? `/api/warehouses?companyId=${user.companyId}`
                : `/api/warehouses?companyId=${user.companyId}&userId=${user.id}`;

            const [wRes, mRes] = await Promise.all([
                fetch(warehouseUrl),
                fetch(`/api/stock-movements?companyId=${user.companyId}&limit=5`)
            ]);

            if (wRes.ok) {
                const data = await wRes.json();
                setWarehouses(data);
                if (data.length > 0 && !formData.warehouseId) {
                    setFormData(prev => ({ ...prev, warehouseId: String(data[0].id) }));
                }

                // Cache products for offline use
                if (isOnline) {
                    fetch(`/api/products?companyId=${user.companyId}&limit=1000`)
                        .then(r => r.json())
                        .then(data => {
                            const products = Array.isArray(data) ? data : (data.products || []);
                            offlineManager.saveProducts(products);
                        })
                        .catch(e => console.error("Offline cache error:", e));
                }
            }
            if (mRes.ok) {
                const mData = await mRes.json();
                if (mData && typeof mData === 'object' && !Array.isArray(mData)) {
                    setRecentMovements(mData.movements || []);
                } else if (Array.isArray(mData)) {
                    setRecentMovements(mData);
                } else {
                    setRecentMovements([]);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }, [user, formData.warehouseId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchLocations = async () => {
            if (!formData.warehouseId || !user?.companyId) {
                setLocations([]);
                return;
            }
            try {
                const res = await fetch(`/api/locations/with-path?warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
                if (res.ok) {
                    const all = await res.json();
                    setLocations(all.filter(l => l.type === 'Slot'));
                }
            } catch (e) {
                console.error("Error fetching locations:", e);
            }
        };
        fetchLocations();
    }, [formData.warehouseId, user?.companyId]);

    // Cleanup scanner
    useEffect(() => {
        return () => {
            if (html5QrCode.current && html5QrCode.current.isScanning) {
                html5QrCode.current.stop().catch(console.error);
            }
        };
    }, []);

    const startScanner = async () => {
        setCameraError('');
        setIsScanning(true);
        try {
            html5QrCode.current = new Html5Qrcode("reader");
            await html5QrCode.current.start(
                { facingMode: "environment" },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                },
                (decodedText) => {
                    handleCodeFound(decodedText);
                },
                (errorMessage) => { }
            );
        } catch (err) {
            console.error(err);
            setCameraError("Caméra indisponible ou bloquée.");
            setIsScanning(false);
        }
    };

    const stopScanner = async () => {
        if (html5QrCode.current && html5QrCode.current.isScanning) {
            try {
                await html5QrCode.current.stop();
                setIsScanning(false);
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleCodeFound = async (code) => {
        stopScanner();
        setScannedCode(code);
        setSearchQuery(code);
        searchProduct(code);
    };

    const handleManualSearch = (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setScannedCode(searchQuery);
        searchProduct(searchQuery);
    };

    const searchProduct = async (skuOrRef) => {
        if (!skuOrRef || !formData.warehouseId) return;
        setLoading(true);
        setSelectedProduct(null);
        setOffProduct(null);
        setSearchResults([]);
        setUnknownProduct(false);
        setManualForm({ name: '', description: '' });
        setCapturedImage(null);
        setImagePreview(null);

        try {
            // 1. Local Search (Refs, Name, Description)
            let prodData = [];
            let usedOffline = !isOnline;

            if (isOnline) {
                try {
                    const prodRes = await fetch(`/api/products/search?sku=${skuOrRef}&warehouseId=${formData.warehouseId}&companyId=${user.companyId}`);
                    const data = await prodRes.json();

                    // Si le Service Worker a renvoyé une erreur custom (offline)
                    if (data && data.offline) {
                        usedOffline = true;
                        prodData = await offlineManager.searchProducts(skuOrRef, user.companyId, formData.warehouseId);
                    } else {
                        prodData = Array.isArray(data) ? data : [];
                    }
                } catch (e) {
                    usedOffline = true;
                    prodData = await offlineManager.searchProducts(skuOrRef, user.companyId, formData.warehouseId);
                }
            } else {
                prodData = await offlineManager.searchProducts(skuOrRef, user.companyId, formData.warehouseId);
            }

            if (prodData && prodData.length > 1) {
                // Multiple matches: show the selection list
                setSearchResults(prodData);
                showToast(`${prodData.length} produits trouvés ${usedOffline ? '(Offline)' : ''}`, "info");
            } else if (prodData && prodData.length === 1) {
                const product = prodData[0];
                setSelectedProduct(product);
                setFormData(prev => ({
                    ...prev,
                    sku: product.sku,
                    locationId: product.locations && product.locations.length > 0 ? String(product.locations[0].id) : ''
                }));
                showToast(`Produit trouvé : ${product.name} ${usedOffline ? '(Offline)' : ''}`, "success");
            } else {
                // 2. No results
                if (!isOnline) {
                    setUnknownProduct(true);
                    showToast("Produit inconnu (Offline)", "warning");
                    return;
                }

                // Ask for External Search (Only if online)
                showConfirm({
                    title: 'Produit non trouvé localement',
                    message: `Souhaitez-vous lancer une recherche sur les bases de données externes (OpenFoodFacts, etc.) pour le code "${skuOrRef}" ?`,
                    confirmText: 'Chercher en ligne',
                    cancelText: 'Saisie manuelle',
                    type: 'info',
                    onConfirm: async () => {
                        setLoading(true);
                        try {
                            const extRes = await fetch(`/api/products/proxy-external/${skuOrRef}?companyId=${user.companyId}`);
                            const extData = await extRes.json();

                            if (extRes.ok && extData.found) {
                                setOffProduct({ ...extData, sku: skuOrRef });
                                showToast(`Trouvé sur ${extData.source}`, "info");
                            } else {
                                setUnknownProduct(true);
                                showToast("Introuvable même en ligne", "warning");
                            }
                        } catch (e) {
                            showToast("Erreur recherche externe", "error");
                        } finally {
                            setLoading(false);
                        }
                    },
                    onCancel: () => {
                        setUnknownProduct(true);
                    }
                });
            }
        } catch (err) {
            console.error(err);
            showToast("Erreur lors de la recherche", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProduct = async (status = 'Actif', offData = null) => {
        if (!formData.warehouseId) return;
        if (status === 'Actif' && !offData && !manualForm.name.trim()) {
            showToast("Veuillez saisir un nom pour le produit", "warning");
            return;
        }

        setLoading(true);
        try {
            const productBody = {
                name: offData ? offData.name : (manualForm.name || `Produit Inconnu (${scannedCode})`),
                sku: scannedCode,
                barcode: scannedCode,
                companyId: user.companyId,
                warehouseId: formData.warehouseId,
                status: status,
                description: offData
                    ? `Trouvé via ${offData.source} (Marque: ${offData.brand})`
                    : (manualForm.description || 'Créé via scanner mobile'),
                quantity: 0,
                userId: user.id
            };

            if (isOnline) {
                let imageUrl = offData ? offData.imageUrl : null;
                if (capturedImage) {
                    const uploadFormData = new FormData();
                    uploadFormData.append('image', capturedImage);
                    const uploadRes = await fetch('/api/upload/product-image', {
                        method: 'POST',
                        body: uploadFormData
                    });
                    if (uploadRes.ok) {
                        const uploadData = await uploadRes.json();
                        imageUrl = uploadData.imageUrl;
                    }
                }
                productBody.imageUrl = imageUrl;

                const productRes = await fetch('/api/products', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(productBody)
                });

                if (productRes.ok) {
                    const newProduct = await productRes.json();
                    setSelectedProduct(newProduct);
                    setOffProduct(null);
                    setUnknownProduct(false);
                    setCapturedImage(null);
                    setImagePreview(null);
                    setManualForm({ name: '', description: '' });
                    showToast(status === 'Brouillon' ? "Brouillon créé" : "Produit créé", "success");
                } else {
                    const error = await productRes.json();
                    showToast(error.error || "Erreur de création", "error");
                }
            } else {
                // Offline Queueing
                const finalProductBody = { ...productBody };
                if (capturedImage) {
                    finalProductBody.capturedImage = capturedImage; // Store the Blob/File in IDB
                }
                const tempId = await offlineManager.queueProduct(finalProductBody);
                setSelectedProduct({ ...finalProductBody, id: `temp_${tempId}`, tempId: tempId, locations: [] });
                setOffProduct(null);
                setUnknownProduct(false);
                setCapturedImage(null);
                setImagePreview(null);
                setManualForm({ name: '', description: '' });
                showToast("Produit créé localement (En attente de synchro)", "info");
            }
        } catch (error) {
            console.error(error);
            showToast("Erreur lors de la création", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleMovement = async (type) => {
        if (!selectedProduct) {
            showToast("Veuillez d'abord scanner ou chercher un produit", "warning");
            return;
        }
        if (!formData.warehouseId || formData.quantity <= 0 || !formData.unit) {
            showToast("Veuillez remplir quantité et unité", "warning");
            return;
        }

        setLoading(true);

        const movementData = {
            productId: selectedProduct.id && !String(selectedProduct.id).startsWith('temp_') ? selectedProduct.id : null,
            tempProductId: selectedProduct.tempId || null,
            warehouseId: formData.warehouseId,
            userId: user.id,
            companyId: user.companyId,
            type: type,
            quantity: formData.quantity,
            unit: formData.unit,
            locationId: formData.locationId === "" ? null : formData.locationId,
            description: formData.description
        };

        try {
            if (isOnline) {
                const response = await fetch('/api/stock-movements', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(movementData)
                });

                if (response.ok) {
                    showToast(`Opération ${type} réussie`, "success");
                    setFormData(prev => ({ ...prev, quantity: 1, description: '' }));
                    setSelectedProduct(null);
                    setSearchQuery('');
                    setScannedCode('');
                    fetchData();
                } else {
                    const error = await response.json();
                    showToast(error.error || "Erreur lors de l'enregistrement", "error");
                }
            } else {
                // Queue for later
                await offlineManager.queueMovement(movementData);
                showToast(`Opération ${type} mise en attente (Hors-ligne)`, "info");

                // Optimistic UI clear
                setFormData(prev => ({ ...prev, quantity: 1, description: '' }));
                setSelectedProduct(null);
                setSearchQuery('');
                setScannedCode('');
            }
        } catch (error) {
            console.error(error);
            if (!isOnline) {
                await offlineManager.queueMovement(movementData);
                showToast(`Opération mise en attente (Erreur réseau)`, "info");
            } else {
                showToast("Erreur lors de l'enregistrement", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-300">

            {/* Caméra & Recherche */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">qr_code_scanner</span>
                    Identifier le produit
                </h2>

                {/* Entrepôt de travail */}
                <div className="mb-4">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Entrepôt Actif</label>
                    <select
                        className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-sm font-bold"
                        value={formData.warehouseId}
                        onChange={e => {
                            setFormData({ ...formData, warehouseId: e.target.value, locationId: '', sku: '' });
                            setSelectedProduct(null);
                        }}
                    >
                        <option value="">Sélectionnez un entrepôt</option>
                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                </div>

                {cameraError && (
                    <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm border border-red-100 dark:border-red-800/30">
                        {cameraError}
                    </div>
                )}

                <div
                    id="reader"
                    className="w-full bg-slate-100 dark:bg-slate-800 rounded-xl overflow-hidden mb-4"
                    style={{ minHeight: isScanning ? '300px' : '0' }}
                ></div>

                <div className="flex gap-2">
                    {!isScanning ? (
                        <button
                            onClick={startScanner}
                            className="flex-1 py-3 bg-primary text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary/20 active:scale-[0.98] transition-transform text-sm"
                        >
                            <span className="material-symbols-outlined text-[20px]">photo_camera</span>
                            Scanner
                        </button>
                    ) : (
                        <button
                            onClick={stopScanner}
                            className="flex-1 py-3 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform text-sm"
                        >
                            <span className="material-symbols-outlined text-[20px]">cancel</span>
                            Arrêter
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-4 my-4">
                    <div className="h-px bg-slate-200 dark:bg-slate-800 flex-1"></div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">OU</span>
                    <div className="h-px bg-slate-200 dark:bg-slate-800 flex-1"></div>
                </div>

                <form onSubmit={handleManualSearch} className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Réf., Code barre..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 h-12 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary outline-none text-sm"
                    />
                    <button
                        type="submit"
                        disabled={loading || !formData.warehouseId}
                        className="h-12 px-6 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold active:scale-[0.98] transition-transform text-sm disabled:opacity-50"
                    >
                        {loading ? '...' : 'Chercher'}
                    </button>
                </form>
            </div>

            {/* Liste de résultats (si plusieurs matches) */}
            {searchResults.length > 1 && !selectedProduct && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                        <h3 className="font-bold text-sm flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-sm">list_alt</span>
                            Choisir le produit ({searchResults.length})
                        </h3>
                        <button onClick={() => setSearchResults([])} className="text-slate-400 hover:text-rose-500">
                            <span className="material-symbols-outlined text-sm">close</span>
                        </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                        {searchResults.map(p => (
                            <button
                                key={p.id}
                                onClick={() => {
                                    setSelectedProduct(p);
                                    setSearchResults([]);
                                    setFormData(prev => ({
                                        ...prev,
                                        sku: p.sku,
                                        locationId: p.locations && p.locations.length > 0 ? String(p.locations[0].id) : ''
                                    }));
                                }}
                                className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                            >
                                <div className="size-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                                    <ProductImage src={p.imageUrl} name={p.name} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm text-slate-900 dark:text-white truncate">{p.name}</p>
                                    <div className="flex gap-2 text-[10px] font-medium text-slate-500 uppercase tracking-tighter">
                                        <span>SKU: {p.sku}</span>
                                        {p.barcode && <span>• EAN: {p.barcode}</span>}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="text-xs font-black text-primary">{p.quantity} {p.unit || 'un'}</p>
                                    <p className="text-[10px] text-slate-400 truncate max-w-[80px]">{p.warehouse?.name}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Produit Trouvé & Actions */}
            {selectedProduct && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-emerald-200 dark:border-emerald-900 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="size-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold overflow-hidden">
                            <ProductImage src={selectedProduct.imageUrl} name={selectedProduct.name} />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-black text-slate-900 dark:text-white leading-tight">{selectedProduct.name}</h3>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">{selectedProduct.sku}</p>
                            <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                Stock actuel : {selectedProduct.currentStock} {selectedProduct.category?.unit || 'Unité(s)'}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {/* Emplacement */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Emplacement Précis (Optionnel)</label>
                            <select
                                className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-sm font-semibold"
                                value={formData.locationId}
                                onChange={e => setFormData({ ...formData, locationId: e.target.value })}
                            >
                                <option value="">Par défaut</option>
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>{loc.fullPath}</option>
                                ))}
                            </select>
                        </div>

                        {/* Unité & Quantité */}
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Unité</label>
                                <select
                                    className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-primary text-sm font-bold"
                                    value={formData.unit}
                                    onChange={e => setFormData({ ...formData, unit: e.target.value })}
                                >
                                    <option value="Unité">Unité</option>
                                    <option value="Carton">Carton</option>
                                    <option value="Palette">Palette</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Quantité</label>
                                <div className="flex items-center h-12 bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(f => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}
                                        className="w-10 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 font-bold"
                                    >-</button>
                                    <input
                                        type="number"
                                        className="flex-1 h-full w-full text-center bg-transparent outline-none font-black text-slate-900 dark:text-white"
                                        value={formData.quantity}
                                        onChange={e => setFormData(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setFormData(f => ({ ...f, quantity: f.quantity + 1 }))}
                                        className="w-10 h-full flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 font-bold"
                                    >+</button>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                            <button
                                onClick={() => handleMovement('Sortie')}
                                disabled={loading}
                                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white p-3 rounded-xl flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg shadow-rose-500/20 disabled:opacity-50"
                            >
                                <span className="material-symbols-outlined">remove_circle</span>
                                <span className="text-[10px] font-black uppercase">Sortie</span>
                            </button>
                            <button
                                onClick={() => handleMovement('Entrée')}
                                disabled={loading}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white p-3 rounded-xl flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                            >
                                <span className="material-symbols-outlined">add_circle</span>
                                <span className="text-[10px] font-black uppercase">Entrée</span>
                            </button>
                            <button
                                onClick={() => {
                                    showConfirm({
                                        title: 'Confirmer l\'ajustement',
                                        message: `Voulez-vous vraiment fixer la quantité de cet article à ${formData.quantity} ? Cela écrasera le stock actuel.`,
                                        type: 'warning',
                                        onConfirm: () => handleMovement('Ajustement')
                                    });
                                }}
                                disabled={loading}
                                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white p-3 rounded-xl flex flex-col items-center justify-center gap-1 active:scale-95 shadow-lg shadow-amber-500/20 disabled:opacity-50"
                            >
                                <span className="material-symbols-outlined">tune</span>
                                <span className="text-[10px] font-black uppercase">Ajuster</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Produit Trouvé sur OFF */}
            {offProduct && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-blue-200 dark:border-blue-900 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="size-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold overflow-hidden">
                            <ProductImage src={offProduct.imageUrl} name={offProduct.name} />
                        </div>
                        <div className="flex-1">
                            <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 block">Trouvé sur {offProduct.source}</span>
                            <h3 className="font-black text-slate-900 dark:text-white leading-tight">{offProduct.name}</h3>
                            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">{offProduct.sku}</p>
                        </div>
                    </div>

                    <button
                        onClick={() => handleCreateProduct('Actif', offProduct)}
                        disabled={loading}
                        className="w-full h-12 bg-blue-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 shadow-lg shadow-blue-500/20 disabled:opacity-50"
                    >
                        <span className="material-symbols-outlined">add_circle</span>
                        Créer et Ajouter au Stock
                    </button>
                </div>
            )}

            {/* Produit Inconnu Partout */}
            {unknownProduct && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-amber-200 dark:border-amber-900 animate-in fade-in slide-in-from-bottom-4 duration-300">
                    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100 dark:border-slate-800">
                        <div className="size-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                            <span className="material-symbols-outlined">help_outline</span>
                        </div>
                        <div className="flex-1">
                            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-1 block">Référence Inconnue</span>
                            <h3 className="font-black text-slate-900 dark:text-white leading-tight">Code : {scannedCode}</h3>
                            <p className="text-xs text-slate-500 font-medium italic">Ce produit n'existe nulle part.</p>
                        </div>
                    </div>

                    <div className="space-y-4 mb-6">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Nom du produit <span className="text-rose-500">*</span></label>
                            <input
                                type="text"
                                placeholder="Indiquez ce que c'est..."
                                className="w-full h-12 px-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold"
                                value={manualForm.name}
                                onChange={e => setManualForm({ ...manualForm, name: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Indices / Description (Optionnel)</label>
                            <textarea
                                placeholder="Couleur, marque, inscriptions sur le carton..."
                                className="w-full p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-sm min-h-[80px]"
                                value={manualForm.description}
                                onChange={e => setManualForm({ ...manualForm, description: e.target.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Photo du carton/produit</label>
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setCapturedImage(file);
                                        setImagePreview(URL.createObjectURL(file));
                                    }
                                }}
                            />
                            {imagePreview ? (
                                <div className="relative group rounded-xl overflow-hidden border-2 border-amber-500/50">
                                    <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover" />
                                    <button
                                        onClick={() => { setCapturedImage(null); setImagePreview(null); }}
                                        className="absolute top-2 right-2 size-8 bg-rose-600 text-white rounded-full flex items-center justify-center shadow-lg"
                                    >
                                        <span className="material-symbols-outlined text-sm">close</span>
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full py-4 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-amber-500 hover:border-amber-500/50 transition-all"
                                >
                                    <span className="material-symbols-outlined text-3xl">photo_camera</span>
                                    <span className="text-[10px] font-bold uppercase">Prendre une photo indice</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => handleCreateProduct('Brouillon')}
                            disabled={loading || !manualForm.name.trim()}
                            className="flex-1 h-12 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                        >
                            <span className="material-symbols-outlined">edit_note</span>
                            Brouillon
                        </button>
                        <button
                            onClick={() => handleCreateProduct('Actif')}
                            disabled={loading || !manualForm.name.trim()}
                            className="flex-1 h-12 bg-amber-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 shadow-lg shadow-amber-500/20"
                        >
                            <span className="material-symbols-outlined">add</span>
                            Création Directe
                        </button>
                    </div>
                    <p className="text-[10px] text-center text-slate-400 mt-4 font-medium italic">Le mode "Brouillon" nécessite un nom pour être identifiable.</p>
                </div>
            )}

            {/* Mouvements récents */}
            {recentMovements.length > 0 && (
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800">
                    <h2 className="text-sm font-bold mb-4 flex items-center gap-2 text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                        <span className="material-symbols-outlined text-[16px]">history</span>
                        Dernières Actions
                    </h2>
                    <div className="space-y-3">
                        {recentMovements.map(m => (
                            <div key={m.id} className="flex flex-col p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                                <div className="flex justify-between items-start mb-1">
                                    <span className="font-bold text-sm text-slate-900 dark:text-white line-clamp-1">{m.product?.name}</span>
                                    <span className={`text-sm font-black whitespace-nowrap ml-2 ${m.type === 'Ajustement' ? 'text-amber-500' : (m.quantity > 0 ? 'text-emerald-600' : 'text-rose-600')}`}>
                                        {m.quantity > 0 ? '+' : ''}{m.rawQuantity || m.quantity} {m.unit || 'un.'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-[10px] text-slate-500 font-medium">
                                    <span className="bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded shadow-sm border border-slate-200 dark:border-slate-700">
                                        {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className="uppercase tracking-wider">{m.warehouse?.name}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
